/**
 * Retail POS Ticket Presentation.
 *
 * LOT 12.8.8.1 — Receipt / Ticket Presentation Contract
 *
 * Pure presentation projection over the authoritative Receipt payload.
 *
 * Input authority:
 * {
 *   company,
 *   receipt,
 *   lines,
 *   payments
 * }
 *
 * This module owns:
 * - no SalesReceipt;
 * - no SalesLine;
 * - no Payment;
 * - no persistence;
 * - no tax calculation;
 * - no payment calculation;
 * - no company scope;
 * - no activity scope;
 * - no API transport;
 * - no print transport;
 * - no PDF generation.
 */

const PRESENTATION_KIND =
  "retail-pos-ticket-presentation";


export function createRetailPOSTicketPresentation(
  receiptSnapshot
) {
  const snapshot =
    normalizeSnapshot(
      receiptSnapshot
    );

  const company =
    freezeRecord(
      snapshot.company
    );

  const receipt =
    freezeRecord(
      snapshot.receipt
    );

  const lines =
    Object.freeze(
      snapshot.lines.map(
        projectLine
      )
    );

  const payments =
    Object.freeze(
      snapshot.payments.map(
        freezeRecord
      )
    );

  const amountExcludingTax =
    normalizeAmount(
      receipt.amount_excluding_tax
    );

  const amountIncludingTax =
    normalizeAmount(
      receipt.amount_including_tax
    );

  const reduction =
    normalizeAmount(
      receipt.reduction
    );

  return Object.freeze({
    kind:
      PRESENTATION_KIND,

    company,

    receipt,

    lines,

    payments,

    identity:
      Object.freeze({
        receiptUuid:
          normalizeRequiredString(
            receipt.uuid,
            "receipt.uuid"
          ),

        receiptNumber:
          normalizeOptionalString(
            receipt.number
          ),

        companyUuid:
          normalizeOptionalString(
            receipt.company_uuid
            ?? company.uuid
          ),

        clientUuid:
          normalizeOptionalString(
            receipt.client_uuid
          ),

        issuedAt:
          normalizeOptionalString(
            receipt.date_issue
          ),
      }),

    totals:
      Object.freeze({
        amountExcludingTax,

        amountIncludingTax,

        vatAmount:
          roundMoney(
            amountIncludingTax
            - amountExcludingTax
          ),

        reduction,
      }),
  });
}


export function isRetailPOSTicketPresentation(
  value
) {
  return Boolean(
    value
    && typeof value === "object"
    && value.kind
      === PRESENTATION_KIND
    && value.identity
    && typeof value.identity
      === "object"
    && typeof value.identity.receiptUuid
      === "string"
    && value.identity.receiptUuid.trim()
      !== ""
    && Array.isArray(value.lines)
    && Array.isArray(value.payments)
  );
}


function normalizeSnapshot(
  value
) {
  if (
    value === null
    || typeof value !== "object"
    || Array.isArray(value)
  ) {
    throw new TypeError(
      "receiptSnapshot must be an object."
    );
  }

  const receipt =
    value.receipt;

  if (
    receipt === null
    || typeof receipt !== "object"
    || Array.isArray(receipt)
  ) {
    throw new TypeError(
      "receiptSnapshot.receipt must be an object."
    );
  }

  const company =
    value.company
    && typeof value.company === "object"
    && !Array.isArray(value.company)
      ? value.company
      : {};

  return {
    company,

    receipt,

    lines:
      Array.isArray(value.lines)
        ? value.lines
        : [],

    payments:
      Array.isArray(value.payments)
        ? value.payments
        : [],
  };
}


function projectLine(
  line
) {
  const source =
    line
    && typeof line === "object"
    && !Array.isArray(line)
      ? line
      : {};

  /*
   * Canonical presentation aliases only.
   *
   * No tax or commercial value is recomputed here.
   * Backend values remain authoritative.
   */
  return Object.freeze({
    ...source,

    name:
      source.wording
      ?? source.name
      ?? "",

    priceExcludingTax:
      normalizeAmount(
        source.unit_price
        ?? source.price_ht
      ),

    amountExcludingTax:
      normalizeAmount(
        source.amount_excluding_tax
      ),

    amountIncludingTax:
      normalizeAmount(
        source.amount_including_tax
      ),

    vatRate:
      normalizeAmount(
        source.vat_rate
      ),

    reduction:
      normalizeAmount(
        source.reduction
      ),

    activityUuid:
      normalizeOptionalString(
        source.activity_uuid
      ),

    activityProductUuid:
      normalizeOptionalString(
        source.activity_product_uuid
      ),
  });
}


function freezeRecord(
  value
) {
  const source =
    value
    && typeof value === "object"
    && !Array.isArray(value)
      ? value
      : {};

  return Object.freeze({
    ...source,
  });
}


function normalizeRequiredString(
  value,
  fieldName
) {
  if (
    typeof value !== "string"
    || value.trim() === ""
  ) {
    throw new TypeError(
      `${fieldName} must be a non-empty string.`
    );
  }

  return value.trim();
}


function normalizeOptionalString(
  value
) {
  if (
    typeof value !== "string"
  ) {
    return null;
  }

  const normalized =
    value.trim();

  return normalized || null;
}


function normalizeAmount(
  value
) {
  const amount =
    Number(value ?? 0);

  return Number.isFinite(amount)
    ? amount
    : 0;
}


function roundMoney(
  value
) {
  return Math.round(
    (Number(value) + Number.EPSILON)
    * 100
  ) / 100;
}
